import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import logoAsset from "@/assets/batts-legal-logo.jpg.asset.json";

const navigation = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  {
    name: "Practice Areas",
    href: "/practice-areas",
  },
  { name: "Industries", href: "/industries" },
  { name: "High-Stakes Matters", href: "/high-stakes" },
  {
    name: "Intelligence",
    href: "/intelligence",
    children: [
      { name: "Competition Policy Intelligence", href: "/intelligence" },
      { name: "Merger Risk Radar", href: "/merger-risk-radar" },
      { name: "Enforcement Risk Index", href: "/enforcement-index" },
      { name: "Strategy Briefings", href: "/strategy-briefings" },
      { name: "Market Structure Analysis", href: "/market-structure" },
    ],
  },
  { name: "Representative Issues", href: "/representative-issues" },
  { name: "Insights", href: "/insights" },
  { name: "Contact", href: "/contact" },
];

const SiteHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdown, setDropdown] = useState<string | null>(null);
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm" style={{ boxShadow: "var(--shadow-nav)" }}>
      <div className="editorial-container">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center" aria-label="Batts Legal LLP home">
            <img
              src={logoAsset.url}
              alt="Batts Legal LLP"
              width={150}
              height={40}
              className="h-10 w-[150px] object-contain"
            />
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {navigation.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.children && setDropdown(item.name)}
                onMouseLeave={() => setDropdown(null)}
              >
                <Link
                  to={item.href}
                  className={`px-3 py-2 text-[13px] font-medium tracking-wide transition-colors rounded-sm flex items-center gap-1 ${
                    location.pathname === item.href
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.name}
                  {item.children && <ChevronDown className="w-3 h-3" />}
                </Link>
                {item.children && dropdown === item.name && (
                  <div className="absolute top-full left-0 w-64 bg-card border border-border rounded-md py-2 shadow-elevated">
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-4">
            <Link
              to="/client-portal"
              className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Client Portal
            </Link>
          </div>

          <button
            className="xl:hidden text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden bg-card border-t border-border">
          <div className="editorial-container py-4 space-y-1">
            {navigation.map((item) => (
              <div key={item.name}>
                <Link
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  {item.name}
                </Link>
                {item.children?.map((child) => (
                  <Link
                    key={child.href}
                    to={child.href}
                    onClick={() => setMobileOpen(false)}
                    className="block pl-8 py-2 text-sm text-muted-foreground hover:text-foreground"
                  >
                    {child.name}
                  </Link>
                ))}
              </div>
            ))}
            <Link
              to="/client-portal"
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              Client Portal
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;
